function normalizeSpecName(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function findSpec(specs, aliases) {
  if (!Array.isArray(specs)) return null;
  const normalizedAliases = aliases.map(normalizeSpecName);
  return specs.find((spec) => {
    const names = [normalizeSpecName(spec?.key), normalizeSpecName(spec?.label)];
    return names.some((name) => normalizedAliases.includes(name));
  }) || null;
}

function displaySpec(key, label, spec) {
  const value = spec?.value;
  const hasValue = value !== null && value !== undefined && String(value).trim() !== '';
  return {
    key,
    label,
    value: hasValue ? String(value).trim() : '–',
    unit: hasValue && spec?.unit ? String(spec.unit).trim() : null,
  };
}

const workingHeightAliases = [
  'working_height_max', 'working_height', 'Arbeitshöhe max',
  'maximale Arbeitshöhe', 'Arbeitshöhe',
];

const liftHeightAliases = [
  'max_lift_height', 'lift_height_max_mm', 'lifting_height', 'lift_height',
  'Hubhöhe max', 'Hubhöhe max (mm)', 'maximale Hubhöhe', 'Hubhöhe',
];

const driveAliases = [
  'power_source', 'drive_type', 'drive', 'Energiequelle', 'Antriebsart', 'Antrieb',
];

function findHeightSpecs(specs) {
  const workingHeight = findSpec(specs, workingHeightAliases);
  const liftHeight = findSpec(specs, liftHeightAliases);
  return { workingHeight, liftHeight, height: workingHeight || liftHeight };
}

function numericSpecValue(spec) {
  const source = spec?.rawValue ?? spec?.value;
  if (typeof source === 'number') return Number.isFinite(source) ? source : null;
  const normalized = String(source ?? '').trim().replace(/\s/g, '').replace(',', '.');
  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
}

function heightInMeters(spec) {
  const value = numericSpecValue(spec);
  if (value === null) return null;
  const unit = normalizeSpecName(spec?.unit);
  const meters = unit === 'mm' ? value / 1000 : (unit === 'cm' ? value / 100 : value);
  return Number.isFinite(meters) && meters > 0 ? meters : null;
}

export function formatHeightMeters(value) {
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(value);
}

export function parseHeightInput(input) {
  const normalized = String(input ?? '').trim().replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(?:\.\d+)?$/.test(normalized)) return null;
  const value = Number(normalized);
  return Number.isFinite(value) && value > 0 ? value : null;
}

export function getMachineHeight(machine) {
  const { workingHeight, height } = findHeightSpecs(machine?.specs);
  const meters = heightInMeters(height);
  if (meters === null) return null;
  return {
    meters,
    kind: workingHeight ? 'working' : 'lift',
    label: `${formatHeightMeters(meters)} m`,
  };
}

export function getMachineDrive(machine) {
  const drive = findSpec(machine?.specs, driveAliases);
  const label = String(drive?.value ?? '').trim();
  if (!label) return null;
  return { value: normalizeSpecName(label), label };
}

export function machineMeetsMinimumHeight(machine, minimumHeight) {
  const height = getMachineHeight(machine);
  return Boolean(height && height.meters + 0.000001 >= minimumHeight);
}

export function compareMachinesByHeightProximity(first, second, minimumHeight) {
  const firstHeight = getMachineHeight(first)?.meters ?? Number.POSITIVE_INFINITY;
  const secondHeight = getMachineHeight(second)?.meters ?? Number.POSITIVE_INFINITY;
  if (firstHeight === secondHeight) {
    return String(first?.title || '').localeCompare(String(second?.title || ''), 'de');
  }
  const distance = Math.abs(firstHeight - minimumHeight) - Math.abs(secondHeight - minimumHeight);
  if (distance !== 0) return distance;
  return String(first?.title || '').localeCompare(String(second?.title || ''), 'de');
}

export function getMachineCardSpecs(machine) {
  const specs = machine?.specs;
  const { workingHeight, liftHeight, height } = findHeightSpecs(specs);
  const drive = findSpec(specs, driveAliases);
  const capacity = findSpec(specs, [
    'platform_capacity', 'rated_capacity', 'max_load_capacity', 'load_capacity',
    'suction_capacity', 'Plattformtraglast', 'Nenntragfähigkeit', 'Max. Traglast',
    'Maximale Traglast', 'Tragfähigkeit', 'Traglast', 'Korblast', 'Saugkraft / Traglast',
  ]);
  const weight = findSpec(specs, [
    'machine_weight', 'operating_weight', 'weight', 'Eigengewicht',
    'Betriebsgewicht', 'Maschinengewicht', 'Gesamtgewicht', 'Gewicht',
  ]);

  return [
    displaySpec('catalog-height', workingHeight ? 'Arbeitshöhe' : (liftHeight ? 'Hubhöhe' : 'Arbeits-/Hubhöhe'), height),
    displaySpec('catalog-drive', 'Antrieb', drive),
    displaySpec('catalog-capacity', 'Max. Traglast', capacity),
    displaySpec('catalog-weight', 'Eigengewicht', weight),
  ];
}
